import { formatSeverityForDisplay } from '../../../policyholders/insights/TabbedReasonsRecommendations';

export interface FindingSummary {
  readonly id: string;
  readonly description: string;
  /** Normalized severity key, e.g. VERY_HIGH. */
  readonly severity: string;
  /** Display form, e.g. "Very High". */
  readonly severityLabel: string;
}

export interface SeverityCount {
  readonly severity: string;
  readonly label: string;
  readonly count: number;
}

export interface RiskFindings {
  /** Every open finding, from the page envelope. */
  readonly total: number;
  /** How many of them this page actually carried. */
  readonly counted: number;
  /**
   * Whether the page covered every finding. When it did not, the severity
   * breakdown describes only part of the set and cannot be shown beside the
   * total without appearing not to add up.
   */
  readonly isComplete: boolean;
  readonly bySeverity: SeverityCount[];
  readonly top: FindingSummary[];
}

/**
 * Most severe first. The risk service's own vocabulary, not the three buckets
 * a mock happens to show -- an account's findings can land anywhere in it, and
 * collapsing them would misreport what the reader actually has outstanding.
 */
export const SEVERITY_ORDER = [
  'VERY_HIGH',
  'CRITICAL',
  'HIGH',
  'MEDIUM',
  'LOW',
  'INFORMATION',
  'INFO',
] as const;

const rank = (severity: string): number => {
  const index = SEVERITY_ORDER.indexOf(
    severity as (typeof SEVERITY_ORDER)[number]
  );
  // Unknown severities sort last rather than jumping the queue.
  return index === -1 ? SEVERITY_ORDER.length : index;
};

const normalize = (value: unknown): string =>
  typeof value === 'string' ? value.trim().toUpperCase() : '';

const text = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

/**
 * The named entities the risk service's copy actually uses, plus the five XML
 * ones. Deliberately not a full HTML table: anything not listed here is left
 * exactly as it arrived rather than guessed at.
 */
const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  apos: "'",
  gt: '>',
  lt: '<',
  nbsp: ' ',
  quot: '"',
  hellip: '\u2026',
  ldquo: '\u201c',
  lsquo: '\u2018',
  mdash: '\u2014',
  ndash: '\u2013',
  rdquo: '\u201d',
  rsquo: '\u2019',
};

const entityChar = (token: string): string | undefined => {
  if (token.charAt(0) !== '#') {
    return NAMED_ENTITIES[token.toLowerCase()];
  }

  const isHex = token.charAt(1).toLowerCase() === 'x';
  const code = parseInt(
    isHex ? token.slice(2) : token.slice(1),
    isHex ? 16 : 10
  );

  // `String.fromCodePoint` throws outside the Unicode range, and a finding
  // that happens to contain `&#0;` must not take the whole tile down with it.
  if (!code || code > 0x10ffff) {
    return undefined;
  }

  return String.fromCodePoint(code);
};

/**
 * Decodes the entities in a string once, the way a browser would.
 *
 * One pass, not a loop: `&amp;#39;` is a description that means to show the
 * characters `&#39;`, and decoding until nothing changes would turn it into an
 * apostrophe.
 */
const decodeEntities = (value: string): string =>
  value.replace(
    /&(#x?[0-9a-f]+|[a-z]+);/gi,
    (entity, token: string) => entityChar(token) ?? entity
  );

/**
 * Finding descriptions can carry markup -- the Insights table renders them
 * through `Interweave` rather than as plain text. This tile shows a one-line
 * summary, so the tags are stripped and the entities decoded.
 *
 * Both are needed. Stripping alone printed the escapes literally, so a finding
 * about an account read "&#39;Bowen@cowbellcyber.ai&#39;s Account &#39;".
 *
 * Decoding after stripping, never before: `&lt;script&gt;` is a description
 * that means to show the text "<script>", and decoding first would hand it to
 * the tag strip as though it had arrived as an element. What comes out is a
 * React text node either way, so a decoded tag is shown, never parsed.
 */
const toPlainText = (value: unknown): string =>
  decodeEntities(text(value).replace(/<[^>]*>/g, ' '))
    .replace(/\s+/g, ' ')
    .trim();

/** Unwraps the recommendations endpoint's Spring page envelope. */
export const readFindings = (payload: any) => ({
  rows: Array.isArray(payload?.content) ? payload.content : [],
  total:
    typeof payload?.totalElements === 'number' ? payload.totalElements : null,
});

/**
 * Summarises an account's outstanding findings for the risk report tile: how
 * many there are, how they break down by severity, and the few most severe.
 */
export function summariseFindings(payload: any, topCount = 3): RiskFindings {
  const { rows, total } = readFindings(payload);

  const findings: FindingSummary[] = rows
    .map((row: any, index: number) => {
      const severity = normalize(row?.importance);
      return {
        // Same identity precedence the Insights listing uses; rows do not
        // reliably carry a plain `id`.
        id:
          text(row?.findingId) ||
          text(row?.id) ||
          text(row?.reasonName) ||
          `finding-${index}`,
        description:
          toPlainText(row?.reasonDescription) || toPlainText(row?.description),
        severity,
        severityLabel: severity ? formatSeverityForDisplay(row.importance) : '',
      };
    })
    .filter((finding: FindingSummary) => Boolean(finding.description));

  const counts = new Map<string, number>();
  findings.forEach((finding) => {
    if (!finding.severity) {
      return;
    }
    counts.set(finding.severity, (counts.get(finding.severity) ?? 0) + 1);
  });

  const bySeverity = Array.from(counts.entries())
    .map(([severity, count]) => ({
      severity,
      label: formatSeverityForDisplay(severity),
      count,
    }))
    .sort((a, b) => rank(a.severity) - rank(b.severity));

  const top = [...findings]
    .sort((a, b) => rank(a.severity) - rank(b.severity))
    .slice(0, topCount);

  // The page total is the real count; the row count only covers this page.
  const resolvedTotal = total ?? findings.length;

  return {
    total: resolvedTotal,
    counted: findings.length,
    isComplete: findings.length >= resolvedTotal,
    bySeverity,
    top,
  };
}
